import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  clearChessCoachConversation,
  getChessCoachConversation,
  getChessCoachStatus,
  postChessCoachChatWithRetry,
} from '../../api/playing';
import { apiErrorMessage } from '../../utils/apiError';
import Button from '../common/Button';
import CoachMarkdown from './CoachMarkdown';
import {
  pinScrollToBottom,
  stickAfterUserScroll,
  stickAfterUserWheel,
} from './coachChatScroll';
import {
  coachChatExportFilename,
  copyText,
  downloadTextFile,
  formatCoachChatMarkdown,
} from './coachChatExport';
import usePrefersReducedMotion from '../../playing/usePrefersReducedMotion';

/** Chips map 1:1 to Tier-A coach tools (not decorative prompts). */
const SUGGESTIONS_GENERAL = [
  { key: 'pin', defaultValue: 'What is a pin and how do I use it?' },
  { key: 'castling', defaultValue: 'How does castling work?' },
  { key: 'opening', defaultValue: 'What are opening principles for beginners?' },
];

const SUGGESTIONS_GAME = [
  { key: 'lastMoveQuality', defaultValue: 'How was my last move?' },
  { key: 'mistakes', defaultValue: 'What were my mistakes in this game?' },
  { key: 'whatToPlay', defaultValue: 'What should I play?' },
];

function CoachMark({ size = 36 }) {
  const gradId = React.useId().replace(/:/g, '');
  return (
    <span className="playing-coach-avatar" style={{ width: size, height: size }} aria-hidden>
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="40" height="40" rx="12" fill={`url(#${gradId})`} />
        <path
          d="M20 9c-1.2 0-2.2.7-2.6 1.8L15.8 15H12l1.4 2.2c.4.6.4 1.4 0 2L12 21.5h3.2l.6 2.4c.3 1.1 1.3 1.9 2.5 1.9h3.4c1.2 0 2.2-.8 2.5-1.9l.6-2.4H28l-1.4-2.3c-.4-.6-.4-1.4 0-2L28 15h-3.8l-1.6-4.2C22.2 9.7 21.2 9 20 9zm-1.5 18.5h3c.8 0 1.5.7 1.5 1.5v1h-6v-1c0-.8.7-1.5 1.5-1.5z"
          fill="white"
          fillOpacity="0.95"
        />
        <defs>
          <linearGradient id={gradId} x1="6" y1="4" x2="34" y2="36" gradientUnits="userSpaceOnUse">
            <stop stopColor="#0f766e" />
            <stop offset="1" stopColor="#0369a1" />
          </linearGradient>
        </defs>
      </svg>
    </span>
  );
}

/**
 * Anytime ChessDesk Coach chat — general chess, finished games, or live (policy-aware).
 */
export default function ChessCoachPanel({
  gameId = null,
  focusPly = null,
  compact = false,
  embedded = false,
  className = '',
}) {
  const { t } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [policyNote, setPolicyNote] = useState('');
  const [exportNote, setExportNote] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const listRef = useRef(null);
  const exportNoteTimerRef = useRef(null);
  /** Follow new text only while the user has not scrolled up to read history. */
  const stickToBottomRef = useRef(true);
  /** Ignore scroll events caused by our own pinScrollToBottom. */
  const ignoreScrollRef = useRef(false);
  const typeTimerRef = useRef(null);
  const abortTypeRef = useRef(false);
  const msgIdRef = useRef(0);
  const historyLoadGenRef = useRef(0);

  const followBottomIfStuck = useCallback(() => {
    if (!stickToBottomRef.current) return;
    const el = listRef.current;
    if (!el) return;
    ignoreScrollRef.current = true;
    pinScrollToBottom(el);
    requestAnimationFrame(() => {
      ignoreScrollRef.current = false;
    });
  }, []);

  const onMessagesScroll = useCallback(() => {
    if (ignoreScrollRef.current) return;
    stickToBottomRef.current = stickAfterUserScroll(listRef.current);
  }, []);

  const onMessagesWheel = useCallback((e) => {
    // Detach immediately on wheel-up so typing ticks cannot yank the user back
    // before the scroll event updates stickToBottom.
    stickToBottomRef.current = stickAfterUserWheel(
      e.deltaY,
      stickToBottomRef.current,
      listRef.current,
    );
  }, []);

  const onMessagesTouchMove = useCallback(() => {
    // Touch scroll has no reliable delta before layout; sample after the browser moves.
    requestAnimationFrame(() => {
      if (ignoreScrollRef.current) return;
      stickToBottomRef.current = stickAfterUserScroll(listRef.current);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    getChessCoachStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        if (!cancelled) setStatus({ enabled: false, configured: false });
      });
    return () => { cancelled = true; };
  }, []);

  // Instant follow only when stuck — never smooth (CSS smooth + typing = fight).
  useLayoutEffect(() => {
    followBottomIfStuck();
  }, [messages, busy, followBottomIfStuck]);

  useEffect(() => () => {
    abortTypeRef.current = true;
    if (typeTimerRef.current) clearTimeout(typeTimerRef.current);
    if (exportNoteTimerRef.current) clearTimeout(exportNoteTimerRef.current);
  }, []);

  // Load persisted per-game thread (or clear when leaving a game context)
  useEffect(() => {
    abortTypeRef.current = true;
    if (typeTimerRef.current) clearTimeout(typeTimerRef.current);
    stickToBottomRef.current = true;
    setError('');
    setPolicyNote('');
    setExportNote('');
    setBusy(false);

    const gen = ++historyLoadGenRef.current;
    if (gameId == null || gameId === '') {
      setMessages([]);
      setHistoryLoading(false);
      return undefined;
    }

    setHistoryLoading(true);
    setMessages([]);
    getChessCoachConversation(Number(gameId))
      .then((conv) => {
        if (historyLoadGenRef.current !== gen) return;
        const rows = Array.isArray(conv?.messages) ? conv.messages : [];
        setMessages(rows.map((m) => ({
          id: m.id != null ? `db-${m.id}` : `db-${++msgIdRef.current}`,
          role: m.role,
          content: m.content || '',
          intent: m.intent,
          mode: m.mode,
          citations: Array.isArray(m.citations) ? m.citations : [],
          verifiedAnalysis: m.verifiedAnalysis || null,
          focusPly: m.focusPly,
        })));
      })
      .catch((e) => {
        if (historyLoadGenRef.current !== gen) return;
        setMessages([]);
        setError(apiErrorMessage(e, t('playing.coach.historyFailed', {
          defaultValue: 'Could not load saved coach chat for this game.',
        })));
      })
      .finally(() => {
        if (historyLoadGenRef.current === gen) setHistoryLoading(false);
      });
    return undefined;
  }, [gameId, t]);

  const available = !!(status?.enabled && status?.configured);

  const flashExportNote = useCallback((msg) => {
    setExportNote(msg);
    if (exportNoteTimerRef.current) clearTimeout(exportNoteTimerRef.current);
    exportNoteTimerRef.current = setTimeout(() => setExportNote(''), 2500);
  }, []);

  const buildExportMarkdown = useCallback(() => formatCoachChatMarkdown(messages, {
    gameId,
    focusPly,
  }), [focusPly, gameId, messages]);

  const hasExportableContent = messages.some(
    (m) => (m.role === 'user' || m.role === 'assistant') && String(m.content || '').trim(),
  );

  const onExportDownload = useCallback(() => {
    if (busy || !hasExportableContent) {
      flashExportNote(t('playing.coach.exportEmpty', { defaultValue: 'Nothing to export' }));
      return;
    }
    const markdown = buildExportMarkdown();
    downloadTextFile(coachChatExportFilename({ gameId }), markdown);
    flashExportNote(t('playing.coach.exported', { defaultValue: 'Chat downloaded' }));
  }, [buildExportMarkdown, busy, flashExportNote, gameId, hasExportableContent, t]);

  const onExportCopy = useCallback(async () => {
    if (busy || !hasExportableContent) {
      flashExportNote(t('playing.coach.exportEmpty', { defaultValue: 'Nothing to export' }));
      return;
    }
    try {
      const ok = await copyText(buildExportMarkdown());
      flashExportNote(ok
        ? t('playing.coach.copied', { defaultValue: 'Chat copied' })
        : t('playing.coach.copyFailed', { defaultValue: 'Could not copy chat' }));
    } catch {
      flashExportNote(t('playing.coach.copyFailed', { defaultValue: 'Could not copy chat' }));
    }
  }, [buildExportMarkdown, busy, flashExportNote, hasExportableContent, t]);

  const onClearSavedChat = useCallback(async () => {
    if (busy || gameId == null || gameId === '') return;
    const ok = window.confirm(t('playing.coach.clearConfirm', {
      defaultValue: 'Clear the saved coach chat for this game? You can start a new discussion afterward.',
    }));
    if (!ok) return;
    setBusy(true);
    setError('');
    try {
      await clearChessCoachConversation(Number(gameId));
      abortTypeRef.current = true;
      if (typeTimerRef.current) clearTimeout(typeTimerRef.current);
      setMessages([]);
      setPolicyNote('');
      flashExportNote(t('playing.coach.cleared', { defaultValue: 'Saved chat cleared' }));
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.coach.clearFailed', {
        defaultValue: 'Could not clear saved coach chat.',
      })));
    } finally {
      setBusy(false);
    }
  }, [busy, flashExportNote, gameId, t]);

  /** Reveal reply in chunks so it feels like someone typing (not an instant dump). */
  const typeOutReply = useCallback((fullText, meta = {}) => new Promise((resolve) => {
    abortTypeRef.current = false;
    if (typeTimerRef.current) clearTimeout(typeTimerRef.current);
    const id = `a-${++msgIdRef.current}`;
    const text = String(fullText || '');
    const baseMsg = {
      id,
      role: 'assistant',
      mode: meta.mode,
      intent: meta.intent,
      citations: meta.citations || [],
      verifiedAnalysis: meta.verifiedAnalysis || null,
      safety: meta.safety || null,
      clarifyOptions: meta.clarifyOptions || [],
    };

    // Respect OS reduced-motion: show the full reply at once (no caret storm).
    if (reducedMotion) {
      setMessages((prev) => [...prev, { ...baseMsg, content: text, typing: false }]);
      resolve();
      return;
    }

    setMessages((prev) => [
      ...prev,
      { ...baseMsg, content: '', typing: true },
    ]);

    let i = 0;
    const finish = (finalText) => {
      setMessages((prev) => prev.map((m) => (
        m.id === id
          ? {
            ...m,
            content: finalText,
            typing: false,
            verifiedAnalysis: meta.verifiedAnalysis || null,
            intent: meta.intent,
            safety: meta.safety || null,
            clarifyOptions: meta.clarifyOptions || [],
          }
          : m
      )));
      resolve();
    };

    const tick = () => {
      if (abortTypeRef.current) {
        finish(text);
        return;
      }
      if (i >= text.length) {
        finish(text);
        return;
      }
      const remaining = text.length - i;
      // Slightly larger chunks on long replies so it stays readable, not glacial.
      let step = 1;
      if (remaining > 400) step = 4 + Math.floor(Math.random() * 3);
      else if (remaining > 120) step = 2 + Math.floor(Math.random() * 2);
      else step = 1 + Math.floor(Math.random() * 2);

      const ch = text[i];
      if (ch === '\n') step = 1;
      i = Math.min(text.length, i + step);
      const slice = text.slice(0, i);
      setMessages((prev) => prev.map((m) => (
        m.id === id ? { ...m, content: slice, typing: true } : m
      )));

      const last = text[i - 1];
      const pause = last === '\n' ? 55 + Math.random() * 45
        : last === '.' || last === '!' || last === '?' ? 70 + Math.random() * 60
        : last === ',' || last === ';' ? 35 + Math.random() * 25
        : 16 + Math.random() * 18;
      typeTimerRef.current = setTimeout(tick, pause);
    };

    typeTimerRef.current = setTimeout(tick, 40);
  }), [reducedMotion]);

  const send = useCallback(async (rawText) => {
    const text = String(rawText || '').trim();
    if (!text || busy) return;
    if (!available) {
      setError(t('playing.coach.unavailable', {
        defaultValue: 'Chess coach is not enabled yet. Ask your academy admin to configure it.',
      }));
      return;
    }
    setError('');
    // New outbound message: pin to bottom again so the reply is visible.
    stickToBottomRef.current = true;
    const history = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }));
    const userId = `u-${++msgIdRef.current}`;
    setMessages((prev) => [...prev, { id: userId, role: 'user', content: text }]);
    setInput('');
    setBusy(true);
    try {
      const res = await postChessCoachChatWithRetry({
        message: text,
        gameId: gameId != null ? Number(gameId) : undefined,
        focusPly: focusPly != null ? Number(focusPly) : undefined,
        history,
      });
      if (!res?.available) {
        setError(res?.unavailableReason
          || t('playing.coach.unavailable', {
            defaultValue: 'Chess coach is not enabled yet. Ask your academy admin to configure it.',
          }));
        return;
      }
      setPolicyNote(res.policyNote || '');
      await typeOutReply(res.reply || res.coachExplanation?.summary || '', {
        mode: res.mode,
        intent: res.intent,
        citations: res.citations || [],
        verifiedAnalysis: res.verifiedAnalysis || null,
        safety: res.safety || null,
        clarifyOptions: Array.isArray(res.clarifyOptions) ? res.clarifyOptions : [],
      });
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.coach.sendFailed', {
        defaultValue: 'Could not reach the coach. Try again.',
      })));
    } finally {
      setBusy(false);
    }
  }, [available, busy, focusPly, gameId, messages, t, typeOutReply]);

  const suggestions = gameId != null ? SUGGESTIONS_GAME : SUGGESTIONS_GENERAL;
  const showEmpty = messages.length === 0 && available && !busy && !historyLoading;

  const panelMode = embedded ? 'embedded' : (compact ? 'compact' : 'roomy');

  return (
    <div className={`playing-coach-panel playing-coach-panel--${panelMode} ${className}`.trim()}>
      {!embedded && (
        <div className="playing-coach-panel__head">
          <CoachMark size={compact ? 32 : 44} />
          <div className="playing-coach-panel__head-text">
            <div className="playing-coach-panel__title-row">
              <h3 className="playing-coach-panel__title">
                {t('playing.coach.title', { defaultValue: 'Ask AI Coach' })}
              </h3>
              {available && (
                <span className="playing-coach-panel__live">
                  <span className="playing-coach-panel__live-dot" />
                  {t('playing.coach.online', { defaultValue: 'Online' })}
                </span>
              )}
            </div>
            <p className="playing-coach-panel__subtitle">
              {gameId != null
                ? t('playing.coach.subtitleGame', {
                  defaultValue: 'Ask about your last move, mistakes in this game, or what to play next.',
                })
                : t('playing.coach.subtitleGeneral', {
                  defaultValue: 'Ask about rules, tactics, openings, and endgames.',
                })}
            </p>
          </div>
        </div>
      )}

      {(hasExportableContent || (gameId != null && messages.length > 0)) && (
        <div className="playing-coach-panel__export-bar">
          <div className="playing-coach-panel__export-actions">
            {hasExportableContent && (
              <>
                <button
                  type="button"
                  className="playing-coach-panel__export-btn"
                  disabled={busy || historyLoading}
                  onClick={onExportDownload}
                  title={t('playing.coach.exportHint', {
                    defaultValue: 'Download this chat as Markdown',
                  })}
                >
                  {t('playing.coach.export', { defaultValue: 'Export' })}
                </button>
                <button
                  type="button"
                  className="playing-coach-panel__export-btn"
                  disabled={busy || historyLoading}
                  onClick={onExportCopy}
                  title={t('playing.coach.copyHint', {
                    defaultValue: 'Copy this chat to the clipboard',
                  })}
                >
                  {t('playing.coach.copyChat', { defaultValue: 'Copy chat' })}
                </button>
              </>
            )}
            {gameId != null && messages.length > 0 && (
              <button
                type="button"
                className="playing-coach-panel__export-btn"
                disabled={busy || historyLoading}
                onClick={onClearSavedChat}
                title={t('playing.coach.clearHint', {
                  defaultValue: 'Delete the saved coach chat for this game',
                })}
              >
                {t('playing.coach.clearChat', { defaultValue: 'Clear saved' })}
              </button>
            )}
          </div>
          {exportNote && (
            <span className="playing-coach-panel__export-note" role="status" aria-live="polite">
              {exportNote}
            </span>
          )}
        </div>
      )}

      {historyLoading && (
        <p className="playing-coach-panel__policy" role="status">
          {t('playing.coach.loadingHistory', { defaultValue: 'Loading saved chat…' })}
        </p>
      )}

      {status && !available && (
        <div className="playing-banner playing-banner--warning" role="status">
          {t('playing.coach.unavailable', {
            defaultValue: 'Chess coach is not enabled yet. Ask your academy admin to configure it.',
          })}
        </div>
      )}

      {policyNote && (
        <p className="playing-coach-panel__policy">{policyNote}</p>
      )}

      <div
        className="playing-coach-panel__messages"
        ref={listRef}
        onScroll={onMessagesScroll}
        onWheel={onMessagesWheel}
        onTouchMove={onMessagesTouchMove}
      >
        {showEmpty && (
          <div className="playing-coach-panel__empty">
            <div className="playing-coach-panel__empty-hero">
              <CoachMark size={56} />
              <p className="playing-coach-panel__empty-title">
                {t('playing.coach.emptyTitle', { defaultValue: 'Ready when you are' })}
              </p>
              <p className="playing-coach-panel__empty-hint">
                {gameId != null
                  ? t('playing.coach.capabilityGame', {
                    defaultValue: 'I can help with: how good a move was, what to play now, and mistakes in this game.',
                  })
                  : t('playing.coach.capabilityGeneral', {
                    defaultValue: 'I can help with: rules, tactics (forks, pins), openings, and endgames.',
                  })}
              </p>
              <p className="playing-coach-panel__empty-hint playing-coach-panel__empty-hint--tools">
                {t('playing.coach.tryAsking', { defaultValue: 'Quick tools:' })}
              </p>
            </div>
            <div className="playing-coach-panel__suggestions">
              {suggestions.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className="playing-coach-chip"
                  disabled={busy}
                  onClick={() => send(t(`playing.coach.suggest.${s.key}`, { defaultValue: s.defaultValue }))}
                >
                  {t(`playing.coach.suggest.${s.key}`, { defaultValue: s.defaultValue })}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id || `${m.role}-${m.content?.slice(0, 12)}`}
            className={`playing-coach-bubble playing-coach-bubble--${m.role}`}
          >
            {m.role === 'assistant' && (
              <CoachMark size={28} />
            )}
            <div className="playing-coach-bubble__body">
              <div className="playing-coach-bubble__label">
                {m.role === 'user'
                  ? t('playing.coach.you', { defaultValue: 'You' })
                  : t('playing.coach.coach', { defaultValue: 'Coach' })}
              </div>
              <div className={`playing-coach-bubble__text${m.typing ? ' playing-coach-bubble__text--typing' : ''}`}>
                {m.role === 'assistant' ? (
                  <CoachMarkdown content={m.content} typing={!!m.typing} />
                ) : (
                  m.content
                )}
              </div>
              {m.role === 'assistant'
                && !m.typing
                && Array.isArray(m.clarifyOptions)
                && m.clarifyOptions.length > 0 && (
                <div className="playing-coach-panel__suggestions playing-coach-panel__suggestions--clarify" role="group" aria-label={t('playing.coach.clarifyOptions', { defaultValue: 'Clarify options' })}>
                  {m.clarifyOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className="playing-coach-chip"
                      disabled={busy}
                      onClick={() => send(opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && messages[messages.length - 1]?.role !== 'assistant' && (
          <div className="playing-coach-bubble playing-coach-bubble--assistant">
            <CoachMark size={28} />
            <div className="playing-coach-bubble__body">
              <div className="playing-coach-bubble__label">
                {t('playing.coach.coach', { defaultValue: 'Coach' })}
              </div>
              <div className="playing-coach-bubble__text playing-coach-bubble__text--thinking">
                <span className="playing-coach-dots" aria-hidden>
                  <span /><span /><span />
                </span>
                {t('playing.coach.thinking', { defaultValue: 'Thinking…' })}
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="playing-banner playing-banner--danger" role="alert">{error}</div>
      )}

      <form
        className="playing-coach-panel__composer"
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
      >
        <textarea
          className="playing-coach-panel__input"
          value={input}
          disabled={busy || !available}
          rows={embedded || compact ? 2 : 3}
          placeholder={t('playing.coach.placeholder', {
            defaultValue: 'Ask the coach…',
          })}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          maxLength={4000}
          aria-label={t('playing.coach.placeholder', { defaultValue: 'Ask the coach…' })}
        />
        <div className="playing-coach-panel__composer-actions">
          <span className="playing-coach-panel__hint">
            {t('playing.coach.enterHint', { defaultValue: 'Enter to send · Shift+Enter for new line' })}
          </span>
          <Button type="submit" variant="page-primary" disabled={busy || !available || !input.trim()}>
            {t('playing.coach.send', { defaultValue: 'Send' })}
          </Button>
        </div>
      </form>
    </div>
  );
}
